import React, {useEffect, useState, useRef, useReducer} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {isEmpty, cloneDeep} from 'lodash/lang';
import {pick} from 'lodash/object';
import LobbyBar from './LobbyBar';
import Instruments from './Instruments';
import {isMobile, getInstrumentDataFromUrl} from './utils';
import indicationArrowSvg from '../images/indication-arrow-up.svg';

const LOBBYBAR_HEIGHT = 48;
const TOOLBAR_HEIGHT = 40;
const LAYOUT_BREAKPOINT = 699;
const INSTRUMENT_BORDER_WIDTH = 2;
const INDICATOR_ARROW_WIDTH = 250;
const INSTRUMENT_SIZE_MAP = Object.entries({
  bode: {width: 1024, height: 652},
  dmm: {width: 320, height: 568},
  fgen: {width: 320, height: 452},
  ia: {width: 1024, height: 652},
  iv: {width: 1024, height: 652},
  logic: {width: 1024, height: 652},
  scope: {width: 1024, height: 652},
  vps: {width: 320, height: 455},
  datalogger: {width: 1024, height: 652},
  datagenerator: {width: 320, height: 560},
  digitalio: {width: 320, height: 460},
}).reduce((obj, [name, size]) => {
  obj[name] = {
    width: size.width + INSTRUMENT_BORDER_WIDTH * 2,
    height: size.height + INSTRUMENT_BORDER_WIDTH * 2,
  };
  return obj;
}, {});
const SCROLL_BAR_WIDTH = isMobile() ? 0 : 8;
const SMALL_INSTRUMENT_MIN_WIDTH = 320 + INSTRUMENT_BORDER_WIDTH * 2;
const LARGE_INSTRUMENT_MIN_WIDTH_MAP = Object.freeze({
  bode: 425 + INSTRUMENT_BORDER_WIDTH * 2,
  iv: 425 + INSTRUMENT_BORDER_WIDTH * 2,
  scope: 544 + INSTRUMENT_BORDER_WIDTH * 2,
});

const initialRectDiff = {
  widthDiff: 0,
  heightDiff: 0,
  leftDiff: 0,
  topDiff: 0,
};
const initialStyles = {
  large: {
    width: {
      bode: INSTRUMENT_SIZE_MAP.bode.width,
      iv: INSTRUMENT_SIZE_MAP.iv.width,
      scope: INSTRUMENT_SIZE_MAP.scope.width,
      datalogger: INSTRUMENT_SIZE_MAP.datalogger.width,
    },
    height: {
      bode: INSTRUMENT_SIZE_MAP.bode.height,
      iv: INSTRUMENT_SIZE_MAP.iv.height,
      scope: INSTRUMENT_SIZE_MAP.scope.height,
      datalogger: INSTRUMENT_SIZE_MAP.datalogger.height,
    },
  },
  small: {
    width: 320 + INSTRUMENT_BORDER_WIDTH * 2,
    height: {
      dmm: 430 + INSTRUMENT_BORDER_WIDTH * 2,
      fgen: 430 + INSTRUMENT_BORDER_WIDTH * 2,
      vps: 430 + INSTRUMENT_BORDER_WIDTH * 2,
      datagenerator: 430 + INSTRUMENT_BORDER_WIDTH * 2,
      digitalio: 430 + INSTRUMENT_BORDER_WIDTH * 2,
    },
  },
  largeWrapper: {
    width: Object.entries(LARGE_INSTRUMENT_MIN_WIDTH_MAP).reduce(
      (maxMin, [name, minWidth]) => Math.max(maxMin, minWidth),
      0,
    ),
    height: 652 + INSTRUMENT_BORDER_WIDTH * 2,
    isHorizontal: true,
  },
  smallWrapper: {
    width: 320 + INSTRUMENT_BORDER_WIDTH * 2,
    height: 430 + INSTRUMENT_BORDER_WIDTH * 2,
    isHorizontal: false,
  },
};

const setWrapperHeight = (bentoBoxRoot, instrumentsWrapperRef, isPopoutMode) => {
  const rootHeight = bentoBoxRoot.getBoundingClientRect().height;
  instrumentsWrapperRef.current.style.height = `${rootHeight -
    TOOLBAR_HEIGHT -
    (isPopoutMode ? 0 : LOBBYBAR_HEIGHT)}px`;
};

const adjustIndicatorArrowPosition = (instrumentsWrapperRef, indicatorArrowRef) => {
  const instrumentsButton = instrumentsWrapperRef.current
    .closest('.bentobox-wrapper')
    .querySelector('.toolbar #instrument1');
  const {left, right, width, bottom} = instrumentsButton.getBoundingClientRect();
  const indicatorArrow = indicatorArrowRef.current;
  indicatorArrow.style.display = 'flex';
  indicatorArrow.style.right = `${right}px`;
  indicatorArrow.style.top = `${bottom}px`;
  indicatorArrow.style.left = `${left - (INDICATOR_ARROW_WIDTH - width) / 2}px`;
};

/**
 * Calculate the widths for large instruments
 * @param {Object} parameter {instruments, largeInstrumentTotalWidth, isLargeScreen, isLargeHorizontal}
 * @returns {Object} widths
 */
const calculateLargeInstrumentWidths = ({instruments, largeInstrumentTotalWidth, isLargeScreen, isLargeHorizontal}) => {
  if (!isLargeScreen || !isLargeHorizontal || instruments.length === 1) {
    return instruments.reduce((widths, {name}) => {
      widths[name] = largeInstrumentTotalWidth;
      return widths;
    }, {});
  }
  const orderedWidths = instruments
    .map(({name}) => ({
      name,
      width: LARGE_INSTRUMENT_MIN_WIDTH_MAP[name],
    }))
    .sort((a, b) => b - a);
  let widths = {};
  const sharedWidth =
    (largeInstrumentTotalWidth - orderedWidths.reduce((sum, orderedWidth) => sum + orderedWidth.width, 0)) /
    instruments.length;
  for (const {name, width: minWidth} of orderedWidths) {
    widths[name] = minWidth + sharedWidth;
  }
  return widths;
};

/**
 * Calculate the heights for large instruments
 * @param {Object} parameter {instruments, wrapperHeight, noScrollMinHeightMap, isLargeHorizontal}
 * @returns {Array} [heights, totalHeight]
 */
const calculateLargeInstrumentHeights = ({instruments, wrapperHeight, noScrollMinHeightMap, isLargeHorizontal}) => {
  if (isLargeHorizontal) {
    return [
      instruments.reduce((heights, {name}) => {
        heights[name] = wrapperHeight;
        return heights;
      }, {}),
      wrapperHeight,
    ];
  }
  const maxDefaultHeight = instruments.reduce(
    (height, {name}) => Math.max(height, INSTRUMENT_SIZE_MAP[name].height),
    0,
  );
  return instruments.reduce(
    ([heights, totalHeight], {name}) => {
      heights[name] = noScrollMinHeightMap[name]
        ? Math.max(maxDefaultHeight, noScrollMinHeightMap[name] + INSTRUMENT_BORDER_WIDTH * 2)
        : maxDefaultHeight;
      return [heights, totalHeight + heights[name]];
    },
    [{}, 0],
  );
};

/**
 * Calculate the heights for small instruments
 * @param {Object} parameter {instruments, wrapperHeight, noScrollMinHeightMap, isSmallHorizontal}
 * @returns {Array} [heights, totalHeight]
 */
const calculateSmallInstrumentHeights = ({instruments, wrapperHeight, noScrollMinHeightMap, isSmallHorizontal}) => {
  if (isSmallHorizontal) {
    return [
      instruments.reduce((heights, {name}) => {
        heights[name] = wrapperHeight;
        return heights;
      }, {}),
      wrapperHeight,
    ];
  }
  return instruments.reduce(
    ([heights, totalHeight], {name}) => {
      heights[name] = noScrollMinHeightMap[name]
        ? noScrollMinHeightMap[name] + INSTRUMENT_BORDER_WIDTH * 2
        : INSTRUMENT_SIZE_MAP[name].height;
      return [heights, totalHeight + heights[name]];
    },
    [{}, 0],
  );
};

const setStylesForSingleInstrument = ({setStyles, large, small, wrapperWidth, wrapperHeight, noScrollMinHeightMap}) => {
  const styles = cloneDeep(initialStyles);
  setStyles({
    large: {
      width: {
        ...styles.large.width,
        ...calculateLargeInstrumentWidths({
          instruments: large,
          largeInstrumentTotalWidth: wrapperWidth,
          isLargeScreen: false,
          isLargeHorizontal: false,
        }),
      },
      height: {
        ...styles.large.height,
        ...calculateLargeInstrumentHeights({
          instruments: large,
          wrapperHeight,
          noScrollMinHeightMap,
          isLargeHorizontal: true,
        })[0],
      },
    },
    largeWrapper: {
      width: wrapperWidth,
      height: isEmpty(large) ? 0 : wrapperHeight,
      isHorizontal: false,
    },
    small: {
      width: wrapperWidth,
      height: {
        ...styles.small.height,
        ...calculateSmallInstrumentHeights({
          instruments: small,
          wrapperHeight,
          noScrollMinHeightMap,
          isSmallHorizontal: true,
        })[0],
      },
    },
    smallWrapper: {
      width: wrapperWidth,
      height: isEmpty(small) ? 0 : wrapperHeight,
      isHorizontal: false,
    },
  });
};

const setStylesForSmallScreen = ({setStyles, large, small, wrapperWidth, wrapperHeight, noScrollMinHeightMap}) => {
  const hasOnlyOneInstrument = small.length + large.length === 1;
  if (hasOnlyOneInstrument) {
    setStylesForSingleInstrument({setStyles, large, small, wrapperWidth, wrapperHeight});
    return;
  }
  const styles = cloneDeep(initialStyles);
  const [largeHeight, largeTotalHeight] = calculateLargeInstrumentHeights({
    instruments: large,
    wrapperHeight,
    noScrollMinHeightMap,
    isLargeHorizontal: false,
  });
  const [smallHeight, smallTotalHeight] = calculateSmallInstrumentHeights({
    instruments: small,
    wrapperHeight,
    noScrollMinHeightMap,
    isSmallHorizontal: false,
  });
  const contentWidth =
    smallTotalHeight + largeTotalHeight > wrapperHeight ? wrapperWidth - SCROLL_BAR_WIDTH : wrapperWidth;
  styles.large = {
    width: {
      ...styles.large.width,
      ...calculateLargeInstrumentWidths({
        instruments: large,
        largeInstrumentTotalWidth: contentWidth,
        isLargeScreen: false,
        isLargeHorizontal: false,
      }),
    },
    height: {
      ...styles.large.height,
      ...largeHeight,
    },
  };
  styles.largeWrapper = {
    width: wrapperWidth,
    height: largeTotalHeight,
    isHorizontal: false,
  };
  styles.small = {
    height: {
      ...styles.small.height,
      ...smallHeight,
    },
    width: contentWidth,
  };
  styles.smallWrapper = {
    width: wrapperWidth,
    height: smallTotalHeight,
    isHorizontal: false,
  };
  setStyles(styles);
};

const setStylesForSmallInstrumentsOnly = ({setStyles, small, wrapperWidth, wrapperHeight, noScrollMinHeightMap}) => {
  const styles = cloneDeep(initialStyles);
  styles.largeWrapper = {
    width: 0,
    height: 0,
    isHorizontal: false,
  };
  const isSmallHorizontal = wrapperWidth >= small.length * SMALL_INSTRUMENT_MIN_WIDTH;
  styles.smallWrapper = {
    width: wrapperWidth,
    height: wrapperHeight,
    isHorizontal: isSmallHorizontal,
  };
  const [smallHeight] = calculateSmallInstrumentHeights({
    instruments: small,
    wrapperHeight,
    noScrollMinHeightMap,
    isSmallHorizontal,
  });
  styles.small = {
    width: SMALL_INSTRUMENT_MIN_WIDTH,
    height: {
      ...styles.small.height,
      ...smallHeight,
    },
  };
  setStyles(styles);
};

const setStylesForLargeInstrumentsOnly = ({setStyles, large, wrapperWidth, wrapperHeight, noScrollMinHeightMap}) => {
  const styles = cloneDeep(initialStyles);
  styles.smallWrapper = {
    width: 0,
    height: 0,
    isHorizontal: false,
  };
  const isLargeHorizontal =
    large.length === 1 ||
    wrapperWidth >=
      large.reduce((totalWidth, instrument) => totalWidth + LARGE_INSTRUMENT_MIN_WIDTH_MAP[instrument.name], 0);
  const [largeHeight, largeTotalHeight] = calculateLargeInstrumentHeights({
    instruments: large,
    wrapperHeight,
    noScrollMinHeightMap,
    isLargeHorizontal,
  });
  const largeContentWidth =
    !isLargeHorizontal && largeTotalHeight > wrapperHeight ? wrapperWidth - SCROLL_BAR_WIDTH : wrapperWidth;
  const largeWidth = calculateLargeInstrumentWidths({
    instruments: large,
    largeInstrumentTotalWidth: largeContentWidth,
    isLargeScreen: true,
    isLargeHorizontal,
  });
  styles.large = {
    width: {
      ...styles.large.width,
      ...largeWidth,
    },
    height: {
      ...styles.large.height,
      ...largeHeight,
    },
  };
  styles.largeWrapper = {
    width: wrapperWidth,
    height: wrapperHeight,
    isHorizontal: isLargeHorizontal,
  };
  setStyles(styles);
};

const setStylesForMultiSize = ({setStyles, large, small, wrapperWidth, wrapperHeight, noScrollMinHeightMap}) => {
  const styles = cloneDeep(initialStyles);
  const [smallHeight, smallTotalHeight] = calculateSmallInstrumentHeights({
    instruments: small,
    wrapperHeight,
    noScrollMinHeightMap,
    isSmallHorizontal: small.length === 1,
  });
  const smallWidth = SMALL_INSTRUMENT_MIN_WIDTH;
  const smallWrapperWidth =
    smallTotalHeight > wrapperHeight ? SMALL_INSTRUMENT_MIN_WIDTH + SCROLL_BAR_WIDTH : SMALL_INSTRUMENT_MIN_WIDTH;
  styles.small = {
    width: smallWidth,
    height: {
      ...styles.small.height,
      ...smallHeight,
    },
  };
  styles.smallWrapper = {
    width: smallWrapperWidth,
    height: wrapperHeight,
    isHorizontal: false,
  };
  const isLargeHorizontal =
    large.length === 1 ||
    wrapperWidth - smallWrapperWidth >=
      large.reduce((totalWidth, instrument) => totalWidth + LARGE_INSTRUMENT_MIN_WIDTH_MAP[instrument.name], 0);
  const [largeHeight, largeTotalHeight] = calculateLargeInstrumentHeights({
    instruments: large,
    wrapperHeight,
    noScrollMinHeightMap,
    isLargeHorizontal,
  });
  const largeWrapperWidth = wrapperWidth - smallWrapperWidth;
  const largeContentWidth =
    !isLargeHorizontal && largeTotalHeight > wrapperHeight ? largeWrapperWidth - SCROLL_BAR_WIDTH : largeWrapperWidth;
  const largeWidth = calculateLargeInstrumentWidths({
    instruments: large,
    largeInstrumentTotalWidth: largeContentWidth,
    isLargeScreen: true,
    isLargeHorizontal,
  });
  styles.large = {
    width: {
      ...styles.large.width,
      ...largeWidth,
    },
    height: {
      ...styles.large.height,
      ...largeHeight,
    },
  };
  styles.largeWrapper = {
    width: largeWrapperWidth,
    height: wrapperHeight,
    isHorizontal: isLargeHorizontal,
  };
  setStyles(styles);
};

const updateInstrumentsRect = (instruments, setStyles, instrumentsWrapperRef, isPopoutMode, noScrollMinHeightMap) => {
  const {width: wrapperWidth, height: wrapperHeight} = instrumentsWrapperRef.current.getBoundingClientRect();
  const {large, small} = instruments;
  const setStylesArgus = {setStyles, large, small, wrapperWidth, wrapperHeight, noScrollMinHeightMap};

  if (isPopoutMode) {
    setStylesForSingleInstrument(setStylesArgus);
    return;
  }

  const isLargeScreen = wrapperWidth > LAYOUT_BREAKPOINT;
  if (!isLargeScreen) {
    setStylesForSmallScreen(setStylesArgus);
    return;
  }

  if (isEmpty(large)) {
    setStylesForSmallInstrumentsOnly(setStylesArgus);
  } else if (isEmpty(small)) {
    setStylesForLargeInstrumentsOnly(setStylesArgus);
  } else {
    setStylesForMultiSize(setStylesArgus);
  }
};

const useInstrumentsReducer = ({setSettings}) => {
  const setInstrumentsReducer = (state, action) => {
    const prevLargeAfterRemoved = state.instruments.large
      .filter((instrument) => !action.instrumentsToRemove.has(instrument.name))
      .map((instrument) => ({...instrument, ...initialRectDiff}));
    const prevSmallAfterRemoved = state.instruments.small
      .filter((instrument) => !action.instrumentsToRemove.has(instrument.name))
      .map((instrument) => ({...instrument, ...initialRectDiff}));
    const childWindowNamesAfterRemoved = Object.keys(state.childWindows).filter(
      (instrumentName) => !action.instrumentsToRemove.has(instrumentName),
    );

    const prevLargeNamesAfterRemoved = new Set(prevLargeAfterRemoved.map((instrument) => instrument.name));
    const prevSmallNamesAfterRemoved = new Set(prevSmallAfterRemoved.map((instrument) => instrument.name));
    const largeToAdd = action.instrumentsToAdd.filter(
      (instrument) => !prevLargeNamesAfterRemoved.has(instrument.name) && instrument.size === 'large',
    );
    const smallToAdd = action.instrumentsToAdd.filter(
      (instrument) => !prevSmallNamesAfterRemoved.has(instrument.name) && instrument.size === 'small',
    );

    const large = [...prevLargeAfterRemoved, ...largeToAdd];
    const small = [...prevSmallAfterRemoved, ...smallToAdd];
    const poppedOut = state.instruments.poppedOut
      .filter((instrument) => !action.instrumentsToRemove.has(instrument.name))
      .map((instrument) => ({...instrument, ...initialRectDiff}));

    return {
      ...state,
      instruments: {
        large,
        small,
        poppedOut,
      },
      order: {
        ...state.order,
        large: [
          ...state.order.large.filter((name) => prevLargeNamesAfterRemoved.has(name)),
          ...largeToAdd.map(({name}) => name),
        ],
        small: [
          ...state.order.small.filter((name) => prevSmallNamesAfterRemoved.has(name)),
          ...smallToAdd.map(({name}) => name),
        ],
      },
      childWindows: pick(state.childWindows, childWindowNamesAfterRemoved),
    };
  };

  const reorderInstrumentsReducer = (state, action) => {
    const {dragIdx, dropIdx, size} = action;

    const rectDiffMap = state.instruments[size].reduce((diffMap, {name, widthDiff, heightDiff, leftDiff, topDiff}) => {
      diffMap[name] = {
        widthDiff,
        heightDiff,
        leftDiff,
        topDiff,
      };
      return diffMap;
    }, {});

    const sizedOrder = [...state.order[size]];
    const dragItem = sizedOrder[dragIdx];
    sizedOrder.splice(dragIdx, 1);
    sizedOrder.splice(dropIdx, 0, dragItem);

    const minIdx = Math.min(dragIdx, dropIdx);
    const maxIdx = Math.max(dragIdx, dropIdx);
    const minIdxItem = sizedOrder[maxIdx];
    const maxIdxItem = sizedOrder[minIdx];
    const minIdxItemDiff = {...rectDiffMap[minIdxItem]};
    const maxIdxItemDiff = {...rectDiffMap[maxIdxItem]};
    rectDiffMap[minIdxItem] = {
      ...minIdxItemDiff,
      topDiff: minIdxItemDiff.topDiff + maxIdxItemDiff.heightDiff,
    };
    rectDiffMap[maxIdxItem] = {...maxIdxItemDiff, topDiff: minIdxItemDiff.topDiff};

    return {
      ...state,
      instruments: {
        ...state.instruments,
        [size]: state.instruments[size].map((instrument) => ({...instrument, ...rectDiffMap[instrument.name]})),
      },
      order: {
        ...state.order,
        [size]: sizedOrder,
      },
    };
  };

  const resizeInstrumentsReducer = (state, action) => {
    const {size, order, diff} = action;
    const sizedInstruments = [...state.instruments[size]];
    const sizedOrder = [...state.order[size]];
    const cur = sizedInstruments.find((instrument) => instrument.name === sizedOrder[order]);
    const next = sizedInstruments.find((instrument) => instrument.name === sizedOrder[order + 1]);
    cur.heightDiff += diff;
    next.heightDiff -= diff;
    next.topDiff += diff;

    return {
      ...state,
      instruments: {
        ...state.instruments,
        [size]: sizedInstruments,
      },
    };
  };

  const popoutInstrumentReducer = (state, action) => {
    const {instrument: instrumentToPopOut, childWindow} = action;
    const {name, size} = instrumentToPopOut;

    return {
      ...state,
      instruments: {
        ...state.instruments,
        [size]: state.instruments[size]
          .filter((instrument) => instrument.name !== name)
          .map((instrument) => ({...instrument, ...initialRectDiff})),
        poppedOut: [...state.instruments.poppedOut, instrumentToPopOut],
      },
      order: {
        ...state.order,
        [size]: state.order[size].filter((instrumentName) => instrumentName !== name),
      },
      childWindows: {
        ...state.childWindows,
        [name]: childWindow,
      },
    };
  };

  const setInstrumentsOrderReducer = (state, action) => {
    return {
      ...state,
      order: action.order,
    };
  };

  const setPreconfiguredSetReducer = (state, action) => {
    return {
      ...state,
      preconfiguredSet: action.preconfiguredSet,
    };
  };

  const setInstrumentsInitialSettingsReducer = (state, action) => {
    const {
      instruments: {large, small, poppedOut},
    } = state;
    const {settingsMap} = action;

    return {
      ...state,
      instruments: {
        large: large.map((instrument) => ({...instrument, settings: settingsMap[instrument.name]})),
        small: small.map((instrument) => ({...instrument, settings: settingsMap[instrument.name]})),
        poppedOut: poppedOut.map((instrument) => ({...instrument, settings: settingsMap[instrument.name]})),
      },
    };
  };

  const [instrumentData, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_INSTRUMENTS':
          return setInstrumentsReducer(state, action);
        case 'REORDER_INSTRUMENTS':
          return reorderInstrumentsReducer(state, action);
        case 'RESIZE_INSTRUMENT':
          return resizeInstrumentsReducer(state, action);
        case 'POPOUT_INSTRUMENT':
          return popoutInstrumentReducer(state, action);
        case 'SET_INSTRUMENTS_ORDER':
          return setInstrumentsOrderReducer(state, action);
        case 'SET_PRECONFIGURED_SET':
          return setPreconfiguredSetReducer(state, action);
        case 'SET_INSTRUMENTS_INITIAL_SETTINGS':
          return setInstrumentsInitialSettingsReducer(state, action);
        default:
          return state;
      }
    },
    {
      instruments: {
        large: [],
        small: [],
        poppedOut: [],
      },
      order: {
        large: [],
        small: [],
      },
      preconfiguredSet: new Set(),
      childWindows: {},
    },
  );
  setSettings({instrumentData: cloneDeep(pick(instrumentData, ['instruments', 'order', 'childWindows']))});

  const setInstruments = ({instrumentsToAdd = [], instrumentsToRemove = new Set()}) =>
    dispatch({type: 'SET_INSTRUMENTS', instrumentsToAdd, instrumentsToRemove});

  const reorderInstruments = ({dragIdx, dropIdx, size}) =>
    dispatch({type: 'REORDER_INSTRUMENTS', dragIdx, dropIdx, size});

  const resizeInstrument = ({size, order, diff}) => dispatch({type: 'RESIZE_INSTRUMENT', size, order, diff});

  const popoutInstrument = ({instrument}) => {
    const {name, originalWidth, originalHeight, settings} = instrument;
    const childWindow = window.open(
      `${location.origin}${
        location.pathname
      }?popped-out=true&instrument=${name}&instrument-setting=${encodeURIComponent(settings)}&parent-window-origin=${
        location.origin
      }&display-name=${name}`,
      '_blank',
      `height=${originalHeight},width=${originalWidth},status=no,toolbar=no,menubar=no,location=no`,
    );
    dispatch({type: 'POPOUT_INSTRUMENT', instrument, childWindow});
  };

  const setInstrumentsOrder = ({order}) => dispatch({type: 'SET_INSTRUMENTS_ORDER', order});

  const setPreconfiguredSet = ({preconfiguredSet}) => dispatch({type: 'SET_PRECONFIGURED_SET', preconfiguredSet});

  const instrumentList = [
    ...instrumentData.instruments.large,
    ...instrumentData.instruments.small,
    ...instrumentData.instruments.poppedOut,
  ];
  const instrumentNamesSet = new Set(instrumentList.map((prevInstrument) => prevInstrument.name));
  const addInstrument = (instrumentName, instrumentSetting) => {
    if (!instrumentNamesSet.has(instrumentName)) {
      const {width, height} = INSTRUMENT_SIZE_MAP[instrumentName];
      const size = width > 800 ? 'large' : 'small';
      setInstruments({
        instrumentsToAdd: [
          {
            size,
            name: instrumentName,
            originalWidth: width,
            originalHeight: height,
            ...initialRectDiff,
            settings: instrumentSetting,
          },
        ],
      });
    } else {
      const {childWindows} = instrumentData;
      if (Object.keys(childWindows).find((name) => name === instrumentName)) {
        childWindows[instrumentName].focus();
      }
    }
  };
  const removeInstrument = (instrumentName) => {
    if (instrumentNamesSet.has(instrumentName)) {
      setInstruments({
        instrumentsToRemove: new Set([instrumentName]),
      });
      const childWindow = instrumentData.childWindows[instrumentName];
      if (childWindow) {
        childWindow.close();
      }
    }
  };

  const setInstrumentsInitialSettings = (settingsMap) =>
    dispatch({type: 'SET_INSTRUMENTS_INITIAL_SETTINGS', settingsMap});

  return {
    instrumentData,
    reorderInstruments,
    resizeInstrument,
    popoutInstrument,
    setInstrumentsOrder,
    setPreconfiguredSet,
    addInstrument,
    removeInstrument,
    setInstrumentsInitialSettings,
  };
};

const useStylesReducer = () => {
  const [styles, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'SET_STYLES':
        return {
          ...state,
          ...action.styles,
        };
      default:
        return state;
    }
  }, initialStyles);

  const setStyles = (styles) => dispatch({type: 'SET_STYLES', styles});

  return [styles, setStyles];
};

const useNoScrollMinHeightMapReducer = () => {
  const [noScrollMinHeightMap, dispatch] = useReducer((state, action) => {
    state = {...state};
    state[action.name] = action.noScrollMinHeight;
    return state;
  }, {});

  const updateNoScrollMinHeight = (name, noScrollMinHeight) => dispatch({name, noScrollMinHeight});

  return [noScrollMinHeightMap, updateNoScrollMinHeight];
};

const BentoBox = ({bentoBoxRoot, initialSettings, setSettings}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [instrumentsTabLoaded, setInstrumentsTabLoaded] = useState(false);
  const [deviceTabLoaded, setDeviceTabLoaded] = useState(false);
  const [reorderingSize, setReorderingSize] = useState(null);
  const [hasInstrumentOptions, setHasInstrumentOptions] = useState(true);
  const instrumentsWrapperRef = useRef();
  const largeInstrumentsRef = useRef();
  const indicatorArrowRef = useRef();
  const {
    instrumentData: {instruments, order, preconfiguredSet, childWindows},
    reorderInstruments,
    resizeInstrument,
    popoutInstrument,
    setInstrumentsOrder,
    setPreconfiguredSet,
    addInstrument,
    removeInstrument,
    setInstrumentsInitialSettings,
  } = useInstrumentsReducer({setSettings});
  const [styles, setStyles] = useStylesReducer();
  const [noScrollMinHeightMap, updateNoScrollMinHeight] = useNoScrollMinHeightMapReducer();

  const params = new URLSearchParams(location.search);
  const isPopoutMode = params.get('popped-out') === 'true';
  const isSimulateMode = params.get('simulate') === 'true';
  const {name: poppedOutInstrumentName, settings: poppedOutInstrumentSetting} = getInstrumentDataFromUrl(params);

  useEffect(
    () => {
      if (!scriptLoaded || (!isPopoutMode && (!deviceTabLoaded || !instrumentsTabLoaded))) return;
      if (poppedOutInstrumentName) {
        addInstrument(poppedOutInstrumentName, poppedOutInstrumentSetting);
      } else if (!isEmpty(initialSettings)) {
        const {
          instrumentData: {
            instruments: {large, small, poppedOut},
            order,
            instrumentsInitialSettingsMap = {},
          },
          hasInstrumentOptions,
        } = initialSettings;
        setHasInstrumentOptions(!!hasInstrumentOptions);
        const instrumentTab = instrumentsWrapperRef.current
          .closest('.bentobox-wrapper')
          .querySelector('.toolbar #instrument1')
          .shadowRoot.querySelector('#instrument-tab');

        setPreconfiguredSet({preconfiguredSet: new Set([...large, ...small, ...poppedOut].map(({name}) => name))});
        Promise.all([...large, ...small].map((instrument) => instrumentTab.openInstrumentWithName(instrument.name)))
          .then(() => {
            setInstrumentsInitialSettings(instrumentsInitialSettingsMap);
            setInstrumentsOrder({order});
          })
          .then(() => {
            for (const instrument of poppedOut) {
              instrumentTab.openInstrumentWithName(instrument.name).then(() => {
                instrumentTab.setListItemPopoutIconVisibleWithName(instrument.name, true);
                popoutInstrument({instrument});
              });
            }
          });
      }
    },
    [scriptLoaded, deviceTabLoaded, instrumentsTabLoaded],
  );

  useEffect(
    () => {
      if (
        isPopoutMode ||
        isSimulateMode ||
        !deviceTabLoaded ||
        window.NationalInstruments.measurementsLive.onDevice ||
        !isEmpty(initialSettings)
      ) {
        return;
      }

      const deviceButton = instrumentsWrapperRef.current
        .closest('.bentobox-wrapper')
        .querySelector('.toolbar device-button').$.button;
      deviceButton.open = true;
    },
    [deviceTabLoaded],
  );

  useEffect(
    () => {
      if (
        isPopoutMode ||
        !isSimulateMode ||
        !instrumentsTabLoaded ||
        window.NationalInstruments.measurementsLive.onDevice
      ) {
        return;
      }

      const instrumentButton = instrumentsWrapperRef.current
        .closest('.bentobox-wrapper')
        .querySelector('.toolbar #instrument1').$.button;
      instrumentButton.open = true;
    },
    [instrumentsTabLoaded],
  );

  useEffect(
    () => {
      if (!instrumentsWrapperRef || isEmpty(childWindows)) return;
      const intervalId = setInterval(() => {
        for (const [name, childWindow] of Object.entries(childWindows)) {
          if (childWindow.closed) {
            const instrumentTab = instrumentsWrapperRef.current
              .closest('.bentobox-wrapper')
              .querySelector('.toolbar #instrument1')
              .shadowRoot.querySelector('#instrument-tab');
            instrumentTab.closeInstrumentWithName(name);
          }
        }
      }, 500);
      return () => {
        clearInterval(intervalId);
      };
    },
    [childWindows],
  );

  useEffect(
    () => {
      if (!scriptLoaded) return;
      window.NationalInstruments.measurementsLive.bentoBoxUtils = {
        addInstrument: (newInstrument) => addInstrument(newInstrument.instrumentName),
        removeInstrument: (newInstrument) => removeInstrument(newInstrument.instrumentName),
      };
      const resizeInstruments = () => {
        setWrapperHeight(bentoBoxRoot, instrumentsWrapperRef, isPopoutMode);
        updateInstrumentsRect(instruments, setStyles, instrumentsWrapperRef, isPopoutMode, noScrollMinHeightMap);
        if (indicatorArrowRef.current) {
          adjustIndicatorArrowPosition(instrumentsWrapperRef, indicatorArrowRef);
        }
      };
      resizeInstruments();
      bentoBoxRoot.addEventListener('resize', resizeInstruments);
      return () => {
        bentoBoxRoot.removeEventListener('resize', resizeInstruments);
      };
    },
    [scriptLoaded, instrumentsTabLoaded, instruments, noScrollMinHeightMap],
  );

  let instrumentWrapperRect;
  if (instrumentsWrapperRef.current) {
    instrumentWrapperRect = instrumentsWrapperRef.current.getBoundingClientRect();
  }
  const isLargeScreen = !!instrumentWrapperRect && instrumentWrapperRect.width > LAYOUT_BREAKPOINT;
  const hasLargeInstruments = !isEmpty(instruments.large);
  const hasSmallInstruments = !isEmpty(instruments.small);
  const hasInstruments = hasLargeInstruments || hasSmallInstruments;
  const hasDifferentSizeInstruments = hasLargeInstruments && hasSmallInstruments;

  return (
    <div className="bentobox-wrapper">
      {!isPopoutMode && instrumentsTabLoaded && !hasInstruments && !isSimulateMode && (
        <div ref={indicatorArrowRef} className="indicator-arrow">
          <img src={`${window.NationalInstruments.bentoBox.svgSpriteUrl + indicationArrowSvg.id}`} />
          <div className="text">Click here</div>
          <div className="text">to add instruments</div>
        </div>
      )}
      <LobbyBar
        isPopoutMode={isPopoutMode}
        isSimulateMode={isSimulateMode}
        scriptLoaded={scriptLoaded}
        setScriptLoaded={setScriptLoaded}
        setInstrumentsTabLoaded={setInstrumentsTabLoaded}
        setDeviceTabLoaded={setDeviceTabLoaded}
        instruments={instruments}
        isLargeScreen={isLargeScreen}
      />
      <div
        ref={instrumentsWrapperRef}
        className={classNames({
          'instruments-wrapper': true,
          'multi-size': hasDifferentSizeInstruments,
        })}
      >
        <div
          ref={largeInstrumentsRef}
          className="large-instruments"
          style={{
            height: styles.largeWrapper.height,
            width: styles.largeWrapper.width,
            overflowY: reorderingSize === 'large' || styles.largeWrapper.isHorizontal ? 'unset' : 'auto',
            overflowX: reorderingSize === 'large' || styles.largeWrapper.isHorizontal ? 'unset' : 'hidden',
          }}
        >
          <div
            className={classNames({
              'reordering-overlay': true,
              'is-reordering': reorderingSize === 'large',
              active: reorderingSize !== null,
            })}
          />
          <Instruments
            instruments={instruments.large}
            style={styles.large}
            order={order.large}
            reorderInstruments={reorderInstruments}
            resizeInstrument={resizeInstrument}
            popoutInstrument={popoutInstrument}
            isHorizontal={styles.largeWrapper.isHorizontal}
            isLargeScreen={isLargeScreen}
            isPopoutMode={isPopoutMode}
            isSimulateMode = {isSimulateMode}
            preconfiguredSet={preconfiguredSet}
            updateNoScrollMinHeight={updateNoScrollMinHeight}
            setReorderingSize={setReorderingSize}
            // No need to care about large horizontal case: it is for listen scroll event while large horizontal wrapper will not scroll
            scrollContainerRef={isLargeScreen ? largeInstrumentsRef : instrumentsWrapperRef}
            hasInstrumentOptions={hasInstrumentOptions}
          />
        </div>
        <div
          className="small-instruments"
          style={{
            height: styles.smallWrapper.height,
            width: styles.smallWrapper.width,
            overflowY: reorderingSize === 'small' || styles.smallWrapper.isHorizontal ? 'unset' : 'auto',
            overflowX: reorderingSize === 'small' || styles.smallWrapper.isHorizontal ? 'unset' : 'hidden',
          }}
        >
          <div
            className={classNames({
              'reordering-overlay': true,
              'is-reordering': reorderingSize === 'small',
              active: reorderingSize !== null,
            })}
          />
          <Instruments
            instruments={instruments.small}
            style={styles.small}
            order={order.small}
            reorderInstruments={reorderInstruments}
            resizeInstrument={resizeInstrument}
            popoutInstrument={popoutInstrument}
            isHorizontal={styles.smallWrapper.isHorizontal}
            isLargeScreen={isLargeScreen}
            isPopoutMode={isPopoutMode}
            isSimulateMode = {isSimulateMode}
            preconfiguredSet={preconfiguredSet}
            updateNoScrollMinHeight={updateNoScrollMinHeight}
            setReorderingSize={setReorderingSize}
            hasInstrumentOptions={hasInstrumentOptions}
          />
        </div>
      </div>
    </div>
  );
};

BentoBox.propTypes = {
  bentoBoxRoot: PropTypes.object.isRequired,
  initialSettings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
};

BentoBox.defaultProps = {
  bentoBoxRoot: document.getElementById('bentobox-root'),
  initialSettings: {},
};

export default BentoBox;
